import {_, Fuse, Keys, observable, pluralize, React, ReactDOM} from '@api'
import {Suggest} from '@blueprintjs/select'
import {FilterItemState, IFilterValue} from '@modules/catalog'
import {CatalogRemoteSearcherStore, GotoCatalogItemPayload} from '@modules/catalogSearcher/CatalogRemoteSearcherStore'
import {ICatalogFieldConfig, CatalogImageSourceConfig, CatalogPerspectiveConfig, IWorkspacePreset, LibraryItemConfig, nav, site, ui, WorkspaceConfig, OFFLINE_ID, LibraryItemConfigWrapper, CatalogStore, IFlexLayoutCatalogRootConfig} from '@stores'
import {CatalogInstanceChildStore} from '@stores/catalog/CatalogInstanceChildStore'
import {IMerchSeasonC8} from '@stores/c8/C8ServicesStore'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {action, autorun, computed, reaction, runInAction, toJS, trace, when} from 'mobx'
import {computedFn, IDisposer} from 'mobx-utils'
import {CatalogFieldSearchResult, CatalogSuggestSearchResultType, ICatalogSearchResult} from './CatalogSearchTypes'
import {icons} from '@stores/ui'
import {PictureAsPdf} from '@material-ui/icons'
import { guid } from '@utils'

const PAGEUPDOWN_SIZE = 5

/**
 * Provides client-side searchable data structures to allow for type-ahead search
 *
 */
export class CatalogSearcherStore extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore) {
    super(catalog)

    // console.log(`Construct searcher store`)
    synchronizeStores(() => {
      const {sidebar, dragDrop, catalog, flexLayout} = this

      this.localSearcher = new LocalSearchableCatalogData(catalog)
      this.remoteSearcher = new CatalogRemoteSearcherStore(catalog)

      this._toDispose.push(
        autorun(() => {
          if (!this.popoverOpen) return

          const {setupSearcher, searchableData, catalog: {catalogConfig}} = this
          catalogConfig && setupSearcher(searchableData)
        }, {name: `Setup catalog search once data and configuration have loaded`}),

        reaction(() => dragDrop.isDragging, (isDragging) => {
          if (isDragging) {
            setTimeout(() => this.suggest && this.suggest.setState({isOpen: false}), 20)
          }
        }),

        autorun(() => {
          if (flexLayout.flexEnabled && !flexLayout.isMaximized) return

          const {isShowingModal} = sidebar

          runInAction(() => {
            if (isShowingModal) {
              this.catalogSearchText = catalog.settings.searcherSearchText
              this.searchText = ''
            } else if (this.catalogSearchText) {
              this.searchText = this.catalogSearchText
              this.catalogSearchText = null
            }
          })
        }),

        autorun(() => {
          if (catalog.switchingWorkspaces) {
            runInAction(() => {
              this.remoteSearcher.resultsDict = {}
            })
          }
        }, {name: `Clear remote search results when catalog workspace changes`}))
    })
  }

  _toDispose : IDisposer[] = []

  @action dispose = () => {
    this.localSearcher?.dispose()
    this.remoteSearcher?.dispose()
    this._toDispose.forEach(f => f())
  }

  @observable catalogSearchText: string

  @computed get loading() {
    return this.remoteSearcher?.searching !== false || this.remoteSearcher?.loading !== false
  }

  @observable.ref localSearcher : LocalSearchableCatalogData
  @observable.ref remoteSearcher : CatalogRemoteSearcherStore

  mungedFields_lowercaseUnique = ({action, type, title, actionLabel, keywords, groupLabel, ...rest} : ICatalogSearchResult) => {
    // console.log({action, type, title, actionLabel, keywords, rest})
    const r = _.chain([action, title, actionLabel, groupLabel, keywords]).compact().uniq().map(_.toLower).value()

    return r
  }

  @computed get searchableData() {
    return this.localSearcher.searchableData.map(d => ({...d, munged: this.mungedFields_lowercaseUnique(d)}))
  }

  @computed get searchKeys() {
    return ['munged', 'title', {name: 'actionLabel', weight: 2}, 'pathLabel', 'keywords', 'selectedVlpChildren', 'children.title', 'children.type', 'children.keywords']
  }

  @action private setupSearcher = (searchableData) => {
    const {searchKeys} = this

    this.fuseSearcher = new Fuse<ICatalogSearchResult>(
      searchableData,
      {
        keys: searchKeys,
        isCaseSensitive: false,
        shouldSort: false,
        includeScore: true,
        includeMatches: false,  // turn on when debugging
        threshold: .3,
        ignoreLocation: true,
        findAllMatches: false,
        useExtendedSearch: false,
      })
  }

  @computed get _searchTextSearchResult() : ICatalogSearchResult {
    // Don't allow this to change on keystroke (do it in the suggest result component instead) to avoid recalculating the entire searchResults() array on every keystroke
    return !this.sidebar.isShowingModal && {
      key:'searchTextResult',
      action: 'search',
      icon: 'text-select', /*loading: loadingSearchTextResultsCount,*/
    }
  }

  @computed get _clearSearchTextSearchResult() : ICatalogSearchResult {
    const {settings: {activeSearchText}} = this
    return !this.sidebar.isShowingModal && activeSearchText && {key: 'clearActiveSearch', title: `Clear site search '${activeSearchText}'`, action: 'clearSearch'}
  }

  /**
   * Updates to this array will cause the searcher to repaint all rows and can freeze the UI for a moment.  We want to update the "Search for 'abcd'..." search result AS THE USER TYPES though so we delay the full array update while still updating the observable result for the search text immediately
   */
  @computed get searchResults() : Array<ICatalogSearchResult> {
    const {_clearSearchTextSearchResult, _searchTextSearchResult, localSearcher, remoteSearcher} = this
    const r = [
      ...[_searchTextSearchResult, _clearSearchTextSearchResult].filter(_.identity),
      ...(localSearcher?.localSearchResults ?? []),
      ...(remoteSearcher?.remoteSearchResults ?? []),
    ]

    //trace()
    // console.log(r)
    return r
  }

  @observable.ref fuseSearcher: Fuse<ICatalogSearchResult>

  /** For <Highlighter /> control **/
  @computed get searchWords() {
    return (this.searchQueryText ?? '').split(' ')
  }

  @computed get isSearching() { return !_.isEmpty(this.searchWords)}

  @computed get activeResult() {
    const {selectedItemIndex} = this
    return selectedItemIndex == -1 ? null : this.searchResults.length ? this.searchResults[selectedItemIndex] : null
  }

  get searchText() { return this.catalog.settings.searcherSearchText}
  set searchText(v) {
    this.catalog.settings.searcherSearchText = v
  }

  // get searchText() { return catalog.settings.searcherText ?? '' }
  //
  // set searchText(s) { catalog.settings.searcherText = s}

  get settings() { return this.catalog.settings}
  @computed get searchQueryText() {
    return this.searchText || (this.sidebar.isShowingModal ? '' : this.settings.activeSearchText) || ''
    //return unescape(site.searchText)
  }

  @action clearSearch = () => {
    this.searchText = ''
    if (!this.sidebar.isShowingModal) {
      this.catalog.settings.activeSearchText = ''
    }
  }

  @action onQueryChange = (query: string) => {
    // console.log(`onQueryChange`, query, this.stores._id)
    this.searchText = query
    if(query === '' && !this.sidebar.isShowingModal) {
      this.settings.activeSearchText = query
    }
  }

  @action onActiveItemChange = (activeItem) => {
    this.selectedItemIndex = !activeItem ? -1 : this.searchResults.indexOf(activeItem)
  }

  @observable popoverOpen = false

  get filter() { return this.catalog.filter }
  @action onPopoverOpened = () => {
    this.popoverOpen = true
    this.filter.everVisible = true
  }

  @action onPopoverClosed = () => {
    this.popoverOpen = false
  }

  @action onKeyDown = (inModal = false) => (e: React.KeyboardEvent) => {
    const {
      onLeftArrowHotkey, onRightArrowHotkey, goToNextSearchResult, goToNextSearchResultGroup, goToPreviousSearchResult, goToPreviousSearchResultPage, goToPreviousSearchResultGroup,
      onItemSelect, activeResult, popoverOpen
    } = this
    switch (e.which) {
      /**
       * Page up
       */
      case Keys.PAGE_UP: {
        e.preventDefault()
        //goToPreviousSearchResultPage()
        onLeftArrowHotkey()
        break
      }

      /**
       * Page down
       */
      case Keys.PAGE_DOWN: {
        e.preventDefault()
        //goToNextSearchResultPage()
        onRightArrowHotkey()
        break
      }

      // case Keys.ARROW_UP: {
      //   e.preventDefault()
      //   goToPreviousSearchResult()
      //   break
      // }
      //
      // case Keys.ARROW_DOWN: {
      //   e.preventDefault()
      //   goToNextSearchResult()
      //   break
      // }

      // Don't want to interfere with text selection
      // case Keys.ARROW_LEFT: {
      //   e.preventDefault()
      //   onLeftArrowHotkey()
      //   break
      // }
      //
      // case Keys.ARROW_RIGHT: {
      //   e.preventDefault()
      //
      //   onRightArrowHotkey()
      //
      //   break
      // }

      case Keys.TAB: {
        e.preventDefault()

        e.shiftKey ? goToPreviousSearchResultGroup() : goToNextSearchResultGroup()
        break
      }

      case Keys.ENTER: {
        e.preventDefault()

        if (activeResult) {
          onItemSelect(activeResult)
        }
        break
      }

      // case Keys.ESCAPE: {
      //   if (!popoverOpen) {
      //     this.searchText = ''
      //
      //     if (!inModal) {
      //       site._searchText = ''
      //     }
      //   }
      //   break
      // }

      default: {
        break
      }
    }
  }

  /**
   * A search item has been selected (clicked or enter key) - perform the action for it
   * @param item
   */
  @action onItemSelect = async (item: ICatalogSearchResult) => {
    const {sidebar, stores: {presets}, catalog,
      flexLayout: {isMaximized},
      catalog: {navigateToLibrary, navigateToFilter, c8hierarchy: {navigateTo}, isLibrarySelected, groupBy}, filter, sidebar: {left}, vlp} = this
    this.selectedItemIndex = !item ? -1 : this.searchResults.indexOf(item)
    //this._activeResult = item

    if (!item) return

    const {action, type} = item
    let handled = true
    switch (action) {
      case 'resetWorkspace': {
        filter.resetFilters()
        groupBy.hierarchy = []
        catalog.selection.clear()
        groupBy.catalogItemsGroup.reset()
        catalog.loadCustomViewFields(presets.matchingPreset)
        vlp.hierarchy.clear()
        break
      }

      case 'gotoFilters':
        left.open = true
        left.tab = 1
        break

      case 'clearHierarchy':
        vlp.hierarchy.clear()
        break

      case 'gotoSeasonSelectorField': {
        const {value: {id, name}} = item, {vlp} = catalog
        if (vlp && vlp.isVLPField(type)) {
          navigateTo(item.value)
        }
        else if (filter.state.groups[type]) {
          filter.toggleFilter({key: type, value: name, item: {name, id, exId: id}})
        } else {
          console.warn(`Unhandled gotoSeasonSelectorField`)
        }

        break
      }

      case 'clearFilters':
        filter.resetFilters(false)
        break

      case 'toggleLOD':
        catalog.onCycleLevelOfDetail()
        break

      case 'toggleCardFieldLabels':
        catalog.settings.showCardLabels = !catalog.settings.showCardLabels
        break

      case 'togglePDF':
        catalog.toggleShowPdf()
        break

      case 'clearSelection':
        catalog.selection.clear()
        break

      case 'createCatalogItem':
        await catalog.createItem.createOfflineItem()
        break

      case 'toggleInlineDetails':
        await catalog.selection.toggleSelectedInlineDetails()
        break

      case 'selectOffline':
        catalog.toggleShowOnlyOfflineCatalogItems()
        break
      case 'uploadOffline':
        catalog.uploadAllOfflineItems()
        break

      case 'clearSearch' :
        catalog.settings.activeSearchText = ''
        this.clearSearch()
        break

      case 'search':
        catalog.settings.activeSearchText = this.searchQueryText
        break

      case 'gotoField':
        const {value: {field}} = item as CatalogFieldSearchResult
        navigateToLibrary(field, true)
        break

      case 'gotoLibraryItem':
        const {group, value: {value}} = item
        if (group && value?._id) {
          if (isMaximized) {
            catalog.navigateToLibraryItem(item.value.library.id, item.value.value._id)
          }
          else {
            const {value: {workspace, _id}} = item as GotoCatalogItemPayload,
              {flexLayout: {create: {createItemDetailsTab}, model: {activeTabset}}, workspaces: {workspaceFor}} = this,
              newTab = createItemDetailsTab({
                tabSet: activeTabset,
                name: '',
                workspace: workspace,
                path: [
                  {
                    esIndex: workspaceFor(workspace).catalog.index,
                    where: [{value: _id}]
                  }
                ]
              })
            newTab.selectInTabSet()
          }
        }
        break

      case 'gotoCatalogItem': {
        // this.clearSearch()
        if (isMaximized) {
          if (item.value.workspace) {
            when(() => !catalog.switchingWorkspaces, () => {
              catalog.focusedCatalogItemId = item.value._id
              catalog.navigateToLibraryItem(null, item.value._id, sidebar.right)
            }, {name: `Focus to specific catalog item after catalog workspace has fully switched from prior navigation`})
          } else {
            const {value: {_id}} = item
            catalog.focusedCatalogItemId = _id
            sidebar.right.path = [{where: [{value: _id}]}]
            sidebar.right.open = true
          }
        }
        else {
          const {value: {workspace, _id}} = item as GotoCatalogItemPayload
          const {flexLayout: {activeTab, model: {activeTabset}}} = this;
          const {flexLayout: {create: {createItemDetailsTab}}} = this
          const newTab = createItemDetailsTab({
            tabSet: activeTabset,
            name: '',
            workspace: workspace,
            path : [{
              where: [{value: item.value}]
            }]
          })
          newTab.selectInTabSet()
        }
        break
      }

      default:
        handled = false
        break
    }

    if (!handled) {
      switch (type) {
        case 'workspace': {
          const {value: {_id}} = item as ICatalogSearchResult<WorkspaceConfig>
          catalog.switchToWorkspace(item.value)
          break
        }
        case 'library': {
          const {value: {id}} = item as ICatalogSearchResult<LibraryItemConfig>
          !isLibrarySelected(id) ? navigateToLibrary(id, true) : (left.open = false)
          break
        }

        case 'plan':
        case 'folder':
        case 'collection': {
          navigateTo(item.value)
          break
        }

        case 'filterValue': {
          const {value: {id}, parent: {value: {field}}} = item as ICatalogSearchResult<IFilterValue, ICatalogFieldConfig>
          filter.toggleFilter({key: field, value: id})
          break
        }

        case 'workspaceView':
          presets.loadPreset(null, item.value as IWorkspacePreset)
          break

        case 'merchSeason':
          const {value: {name, id}} = item as ICatalogSearchResult<IMerchSeasonC8>,
            {selected} = filter.state.groups['merchSeason']
          selected.has(name) ? selected.delete(name) : selected.set(name, {id: name, exId: id, name})

          break

        case 'perspective':
          catalog.setPerspective(item.value as CatalogPerspectiveConfig)
          break

        case 'groupBy':
          switch (item.action) {
            case 'groupBy':
              const {value: {field}} = item as CatalogFieldSearchResult,
                {hierarchy} = groupBy

              const isGroupedBy = hierarchy.find(h => h === field) != null
              if (isGroupedBy) {
                groupBy.hierarchy = hierarchy.filter(g => g != field)
              } else {
                if (hierarchy.length >= ui.settings.maxGroupDepth) {
                  hierarchy.splice(hierarchy.length - 1, 1)
                }

                hierarchy.push(field)
              }
              break

            case 'clear' :
              groupBy.hierarchy = []
              break

            case 'expandAll':
              groupBy.expandAll()
              break
            case 'collapseAll':
              groupBy.collapseAll()
              break
          }

          break

        case 'field': {
          const {value: {field}} = item as CatalogFieldSearchResult
          switch (item.action) {
            case 'toggleCardField':
              catalog.cv.toggleCardViewFieldVisible(field)
              break

            case 'sort':
              const {groupBy: {catalogItemsGroup, groupingByName}} = catalog,
                {value} = item,
                hierarchyGroup = groupingByName.get(item.group) ?? groupingByName.get(item.esIndex)

                if (item.group) {
                  const {firstOrderBy: orderBy} = item.group ? hierarchyGroup : catalogItemsGroup,
                     sortByGroup = groupingByName.get(value.field),
                    {fieldCustomSorts} = (sortByGroup || hierarchyGroup),
                    hasCustomSort = fieldCustomSorts && fieldCustomSorts[sortByGroup ? '_key' : value.field]
                  orderBy.order = orderBy.field === item.value.field ? (orderBy.order === 'asc' ? 'desc' : 'asc') : orderBy.order
                  orderBy.field = item.value.field
                  orderBy.custom = hasCustomSort != null
                } else {
                  const {orderBy} = hierarchyGroup
                  const index = orderBy.findIndex(f => f.field === value.field)
                  if (index != -1) {
                    if (orderBy.length === 1) {
                      return
                    }
                    orderBy.splice(index, 1)
                  } else {
                    orderBy.push({field, custom: false, order: 'asc'})
                  }
                  if (orderBy.length > ui.settings.maxCatalogItemOrderByTerms) {
                    orderBy.splice(1, 1)
                  }
                }
              // catalogItemsGroupcatalog.onClickSortMenu({field, custom: item.sortableType === 'custom'})(null)
              // if (item.group) {
              //   const group = catalog.groupBy.groupingByName.get(item.group)
              //   orderBy.order = group.orderBy.field === field ? (group.orderBy.order === 'asc' ? 'desc' : 'asc') : group.orderBy.order
              //   orderBy.field = field
              //   item.sortableType === 'custom' ? (group.orderBy.custom = true) : (group.orderBy.custom = false)
              // } else {
              //   const {catalogItemsGroup: {orderBy}} = groupBy,
              //   isCustom = item.sortableType === 'custom'
              //   orderBy.order = orderBy.field === field && orderBy.custom === isCustom ? (orderBy.order === 'asc' ? 'desc' : 'asc') : orderBy.order
              //   orderBy.field = field
              //   isCustom ? (orderBy.custom = true) : (orderBy.custom = false)
              // }
              break

            default:
              console.warn(`Unhandled action '${item.action}' for field '${field}'`)
          }

          break
        }

        case 'filter': {
          const {value: {field}} = item as CatalogFieldSearchResult

          switch (action) {
            case 'clearFilter': {
              filter.groupFor(field).selected.clear()
              break
            }

            default: {
              navigateToFilter()
              filter.selectAndExpandFilterGroup(field)
              break
            }
          }
          break
        }

        case 'image': {
          const {value: {id}} = item as ICatalogSearchResult<CatalogImageSourceConfig>
          catalog.toggleImageSource(id)
          break
        }
      }
    }
  }

  @computed get defaultIndex() {
    return this.searchText ? 1 : 0
  }

  @observable selectedItemIndex = -1

  @computed get selectedItem() {
    const {selectedItemIndex, searchResults} = this
    return selectedItemIndex >= 0 ? undefined : searchResults[selectedItemIndex]
  }

  @action goToNextSearchResult = () => {
    const {selectedItemIndex, searchResults} = this
    this.selectedItemIndex = searchResults.length
                             ? selectedItemIndex + 1 < searchResults.length
                               ? selectedItemIndex + 1
                               : 0
                             : -1
  }

  @action goToPreviousSearchResult = () => {
    const {selectedItemIndex, searchResults} = this
    this.selectedItemIndex = searchResults.length
                             ? selectedItemIndex > 0
                               ? selectedItemIndex - 1
                               : searchResults.length - 1
                             : -1
  }

  @action goToNextSearchResultPage = () => {
    const {selectedItemIndex, searchResults} = this
    this.selectedItemIndex = searchResults.length
                             ? selectedItemIndex + PAGEUPDOWN_SIZE < searchResults.length
                               ? selectedItemIndex + PAGEUPDOWN_SIZE
                               : searchResults.length - 1
                             : -1
  }

  @action goToPreviousSearchResultPage = () => {
    const {selectedItemIndex, searchResults} = this
    this.selectedItemIndex = searchResults.length
                             ? selectedItemIndex > PAGEUPDOWN_SIZE
                               ? selectedItemIndex - PAGEUPDOWN_SIZE
                               : 0
                             : -1
  }

  @action goToPreviousSearchResultGroup = () => {
    const {selectedItemIndex, searchResults} = this,
      original = searchResults[selectedItemIndex]

    if (selectedItemIndex == 0) {
      this.selectedItemIndex = searchResults.length - 1
    } else {
      if (original?.type == searchResults[selectedItemIndex - 1]?.type) {
        // Middle of a group, find the last prior item with the same type
        this.selectedItemIndex = _.findLastIndex(
          searchResults.slice(0, selectedItemIndex - 1).reverse(),
          v => v.type == original.type) ?? 0
      } else {
        // Find the first entry of the prior group
        const toSearch = searchResults.slice(0, selectedItemIndex).reverse()

        let priorGroupIndex = toSearch.findIndex((v) => v.type != original.type || v.group != original.group),
          // continue on to the last matching item in this group since we're navigating backwards
          endOfGroupItem = toSearch[priorGroupIndex],
          lastIndex = _.findLastIndex(toSearch.slice(priorGroupIndex + 1), v => v.type == endOfGroupItem.type && v.group == endOfGroupItem.group)

        this.selectedItemIndex = lastIndex == -1 ? searchResults.length - 1 : lastIndex
      }
    }
  }

  @action goToNextSearchResultGroup = () => {
    const {selectedItemIndex, searchResults} = this, original = searchResults[selectedItemIndex]

    const nextGroupIndex = selectedItemIndex + 1 + searchResults.slice(selectedItemIndex + 1).findIndex((v) => v.type != original?.type || v.group != original?.group)

    //console.log({selectedItemIndex, nextGroupIndex, current: searchResults[selectedItemIndex], next: searchResults[nextGroupIndex]})
    this.selectedItemIndex = nextGroupIndex == -1 ? 0 : nextGroupIndex
  }

  /***
   * Either jump backward to the prior set of grouped search results, or to the beginning of a group you're in the middle of, OR up a level if you're in a nested popover
   */
  @action onLeftArrowHotkey = () => {
    const {goToPreviousSearchResultGroup} = this
    goToPreviousSearchResultGroup()
  }

  /***
   * Either jump foward to the next set of grouped search results, or if focused on a menuitem with the children then down a level in the search result hierarchy
   */
  @action onRightArrowHotkey = () => {
    const {goToNextSearchResultGroup} = this
    goToNextSearchResultGroup()
  }

  @observable.ref suggest: Suggest<ICatalogSearchResult>
  @action setSuggestRef = r => this.suggest = r

  @action focusAndOpenSearcher = () => {
    const node = ReactDOM.findDOMNode(this.suggest) as HTMLElement
    node.querySelectorAll('input').item(0).focus()
  }

  @action reset = () => {
    this.settings.searcherSearchText = ''
    this.settings.activeSearchText = ''
    this.searchText = ''
    this.catalogSearchText = ''
  }

  @computed get searchableLibraries() : Array<LibraryItemConfigWrapper> {
    const {
      catalog: {
        libraries
      }
    } = this
    return (libraries ?? []).filter(l => !l.divider && l.visible !== false)
  }
}

/**
 * Dynamic Searchable Catalog Data
 */
class LocalSearchableCatalogData extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore) {
    super(catalog)

    synchronizeStores(() => {
      const {workspaces, catalog} = this
      this.filtersGroup = new FiltersSearchGroup(catalog)

      this._toDispose.push(
        autorun(() => {
          const {searcher:{popoverOpen}} = this
        if (!popoverOpen || !workspaces.current) return []

        const {catalogSearchActions, assignableFields, otherWorkspaces, imageSources} = this,
          {sidebar: {modalSidebar}} = this

        runInAction(`Update local searchable data - this changes a lot at startup as filters and such are loaded`, () => {
        /*_.isEmpty(this.searchableData) ? this._updateSearchableData() :*/
          this.updateSearchableData_debounced()
        })
      }, {name: `Update local search data`}),

      autorun(() => {
        const {searcher: {fuseSearcher, popoverOpen}} = this
        if (!catalog.settingsStore?.urlProcessed || !popoverOpen || !workspaces.current) return

        const {_updateSearchResults, updateSearchResults_debounced} = this,
          {catalog: {searcher: {searchQueryText}}} = this

        runInAction(`Update local search results (expensive)`, () => {
          /*!this.hasRunSearch ? _updateSearchResults() : */updateSearchResults_debounced()
        })
      }))
    })
  }

  get searcher() { return this.catalog.searcher}

  _toDispose: IDisposer[] = []

  @action dispose = () => this._toDispose.forEach(f => f())

  @observable hasRunSearch = false
  @action private _updateSearchableData = () => {
    const {catalogSearchActions, assignableFields, otherWorkspaces, imageSources} = this,
      {sidebar: {modalSidebar}} = this

    // console.log(`Update local search data`)
    this.searchableData = _.compact([
      ...(!modalSidebar ? catalogSearchActions : []),
      ...imageSources,
      ...assignableFields,
      // ...otherWorkspaces
      //...searchableFilterFields
    ])
  }

  updateSearchableData_debounced = _.debounce(() => {
    this._updateSearchableData()
  }, 300)

  updateSearchResults_throttle = _.throttle(() => {
    this._updateSearchResults()
  }, 200, {leading: false, trailing: true})

  updateSearchResults_debounced = _.debounce(() => {
    this._updateSearchResults()
  }, 300, {leading: false, trailing: true})

  @action private _updateSearchResults = () => {
    this.hasRunSearch = true
    this.localSearchResults = this._searchResultsFor(this.catalog.settings.searcherSearchText, this.searchableData)
  }

  _searchResultsFor = computedFn((searchQueryText, searchableData: Array<ICatalogSearchResult>) => {
    // console.log(`Calculate local results for `, {searchableData, searchQueryText})
    const {catalog} = this
    if (!catalog.settingsStore.urlProcessed) return []
    const {catalog: {searcher: {fuseSearcher}}} = this

    let r : Array<ICatalogSearchResult>

    if (!fuseSearcher || !searchQueryText) {
      r = searchableData.filter(v => !v.parent)
    }
    else {
      const searchResults = fuseSearcher.search(searchQueryText)
      r = searchResults.map(v => v.item)
    }
    return r
  }, {keepAlive: true})

  @observable.ref localSearchResults : Array<ICatalogSearchResult> = []

  filtersGroup : FiltersSearchGroup

  @computed get catalogLibraries(): Array<ICatalogSearchResult> {
    const {
      strings, catalog: {
        labelForLibrary,
        searcher
      }
    } = this

    return searcher.searchableLibraries.map<ICatalogSearchResult>(l => ({
      key:guid(),
      type: 'library',
      groupLabel: strings.libraries,
      value: l,
      title: labelForLibrary({id: l.id})
    }))
  }

  @computed get otherWorkspaces(): Array<ICatalogSearchResult> {
    const {
      strings, workspaces: {availableWorkspaces, wrapperById, current: currentWs}, flexLayout: {isMaximized}
    } = this

    if (!isMaximized) return []

    return (availableWorkspaces ?? []).map(ws => (ws._id != currentWs) && wrapperById[ws._id].visible && {
      key: guid(),
      type: 'workspace',
      groupLabel: strings.workspaces,
      title: ws.title,
      value: ws
    }).filter(_.identity);
  }

  @computed get imageSources(): Array<ICatalogSearchResult> {
    const {catalog: {imageSourceOptions}} = this
    return (imageSourceOptions?.length <= 1 || !imageSourceOptions?.length) // Don't show image source options if there's only 1 possible source
           ? []
           : imageSourceOptions.map<ICatalogSearchResult>(
        imageSource => ({
          key: guid(),
          type: 'image' as CatalogSuggestSearchResultType,
          action: 'setImageSource',
          actionLabel: 'Image Source',
          groupLabel: 'Image Sources',
          title: imageSource.label,
          value: imageSource
        }))
  }

  @computed get assignableFields(): Array<ICatalogSearchResult> {
    const {
      catalog: {
        labelForFieldName_currentCatalog, assignableFields
      }

    } = this

    return (assignableFields ?? []).map<ICatalogSearchResult>(f => ({
      //type: 'assignableField' as CatalogSuggestSearchResultType,
      key: guid(),
      action: 'gotoField',
      actionLabel: 'Field',
      groupLabel: 'Field Libraries',
      value: f,
      title: labelForFieldName_currentCatalog(f.field)
    }))
  }

  get cardSettings(): Array<ICatalogSearchResult> {
    return [
      {
        key: guid(),
        title: 'More Detail', action: 'toggleLOD', type: 'cardSetting', keywords: ['toggle lod card fields'], groupLabel: 'Card Settings', icon: 'zoom-in'},
      /*catalog.groupBy.levelOfDetail == 1 && */{title: 'Card Field Labels', action: 'toggleCardFieldLabels', type: 'cardSetting', keywords: ['action toggle']}
    ]
  }

  /**
   * We want to be able to display results like
   * When typing 'ver'
   * Placeholder Field: VERsion Name:  List of Available Values
   *
   */
  @computed get cardViewFields(): Array<ICatalogSearchResult> {
    const {strings, catalog: {index, labelForFieldName_currentCatalog, availableCardFields}} = this
    return _.orderBy(availableCardFields, f => labelForFieldName_currentCatalog(f.field)).map(
        field => (
          {
            key: guid(),
            type: 'field',
            action: 'toggleCardField',
            actionLabel: 'Card Field',
            groupLabel: strings.cardFields,
            esIndex: index,
            value: field,
            title: labelForFieldName_currentCatalog(field.field)
          } as ICatalogSearchResult
        )).filter(_.identity)
  }

  /**
   * Dynamic search results for things we need information from C8 to fulfill, but that we locally search as the user types (instead of fetching varying lists from C8 with input search text, for example)
   */
  @computed get seasonalFields(): Array<ICatalogSearchResult> {
    const {strings, catalog: {seasonSelectorFields, c8hierarchy, seasonSelectorsCatalog : ss, labelForFieldName_currentCatalog, seasonSelector: {_counts}, c8hierarchy: {hierarchies}}, vlp: {hierarchy},workspaces: {workspaceConfig: {catalog: {sync}}},site} = this

    return [
      !_.isEmpty(hierarchy.selected.plans) &&
      {
        key: 'clearSeasonalHierarchy', title: 'Clear Hierarchy', type: 'catalog', action: 'clearHierarchy', groupLabel: strings.seasonalHierarachies, keywords: seasonSelectorFields.map(f => labelForFieldName_currentCatalog(f.field))},
      ..._.flatten(
        seasonSelectorFields.map(({field, label}) => {
          let count = _counts[pluralize(field)],
            items = _.uniqBy(hierarchies[field] ?? [], (item: any) => item.name)

          if (field === 'collectionVersion') {
            items = ss?.collectionVersion?.sortBuckets(items) ?? items
          }

          // if (items.length > 500) {
          //   console.warn(`${field} - ${label} - item.length is ${items.length} which is more than the limit of 500, ignoring this field for the searcher`, toJS({items, field, label, idField}))
          //   return []
          // }

          // Do not make this unique YFTS-8183
          return _.orderBy(items, field === 'collectionVersion' && sync?.showVersions==true ? undefined: v => `${v.depth}_${v.name}`)
              .map<ICatalogSearchResult>(
                (v) => {
                  const c = !_.isEmpty(count) ? count[v['exId'] ?? v['id']] ?? 0 : undefined,
                    isSelected = hierarchy.isSelected(field, v.id)

                  return (field === 'collection' || !count || count[v.id] || isSelected) && ({
                    key: guid(),
                    type: field as CatalogSuggestSearchResultType,
                    action: 'gotoSeasonSelectorField',
                    value: {...v, count: c},
                    keywords: ['hierarchy', field, 'season'],
                    actionLabel: `${_.startCase(label || field)}${field == 'folder' && _.has(v, 'depth') ? ` ${v.depth + 1}` : ''}`,
                    groupLabel: `${pluralize(_.startCase(label || field))}`,
                    pathLabel: c8hierarchy.pathToHierarchyItem(v.id).slice(0, -1).map(i => i.name).join(' → '),
                    selectedVlpChildren: !isSelected ? [] : hierarchy.findSelectedChildren(field, v.id).map(c => {
                      if (_.isArray(c)) { return c.map(i => i.name).join(' → ')}
                      return c.name
                    }).filter(_.identity),
                    title: v.name,
                  } as ICatalogSearchResult)
                }).filter(v => v && v.title)
        })) as Array<ICatalogSearchResult>

    ].filter(_.identity)
  }

  @computed get groupByFields(): Array<ICatalogSearchResult> {
    const {catalog: {index, groupBy: {groupByFields, catalogItemsGroup}, labelForFieldName_currentCatalog}} = this

    return _.orderBy(
        groupByFields.filter(field => field.field != catalogItemsGroup?.fieldName),
        f => labelForFieldName_currentCatalog(f.field)).map(
        field => (
          {
            key: guid(),
            group: 'groupBy',
            groupLabel: 'Groupable Fields',
            type: 'groupBy',
            action: 'groupBy',
            actionLabel: 'Group By',
            esIndex: index,
            value: field,
            title: labelForFieldName_currentCatalog(field.field)
          } as ICatalogSearchResult
        )).filter(_.identity)
  }

  @computed get groupBySortFields(): Array<ICatalogSearchResult> {
    const {strings, catalog: {index, groupBy: {groupingByName, hierarchy}, _fieldByName, labelForFieldName_currentCatalog}} = this
    return hierarchy.reduce((acc, h) => {
      const group = groupingByName.get(h)
      group?.sortByFields?.forEach(f => {
        const isDefaultFields = (f.field === '_count' || f.field === '_key'),
          field = _fieldByName[group.fieldName],
          title = isDefaultFields ? f.label : labelForFieldName_currentCatalog(f.field),
          label = group.title
        //console.log(label, isDefaultFields, f.field)
        field && acc.push(
          {
            key: guid(),
            type: 'field',
            action: 'sort',
            sortableType: 'alphabetical',
            actionLabel: strings.sortBy,
            group: field.field,
            groupLabel: `${label} Sortable Fields`,
            keywords: 'order reverse',
            esIndex: index,
            value: f,
            title
          },
          (group.fieldCustomSorts && f.field !== '_count') ? {
            key: guid(),
            type: 'field',
            action: 'sort',
            sortableType: 'custom',
            actionLabel: strings.sortBy,
            group: field.field,
            groupLabel: `${label} Sortable Fields`,
            keywords: 'order reverse',
            esIndex: index,
            value: f,
            title
          } : undefined)
      })
      return acc
    }, []).filter(_.identity)
  }

  @computed get sortFields(): Array<ICatalogSearchResult> {
    const {strings, catalog: {index, catalogItemOrderByFields, labelForFieldName_currentCatalog, sortBySection}} = this
    return catalogItemOrderByFields.reduce((acc, f) => {
      acc.push(
        {
          key: guid(),
          type: 'field',
          action: 'sort',
          sortableType: 'alphabetical',
          actionLabel: strings.sortBy,
          groupLabel: 'Sortable Fields',
          keywords: 'order reverse',
          esIndex: index,
          value: f,
          title: labelForFieldName_currentCatalog(f.field)
        }, f.sort?.custom ? {
          key: guid(),
          type: 'field',
          action: 'sort',
          sortableType: 'custom',
          actionLabel: strings.sortBy,
          groupLabel: 'Sortable Fields',
          keywords: 'order reverse',
          esIndex: index,
          value: f,
          title: labelForFieldName_currentCatalog(f.field)
        } : undefined)
      return acc
    }, []).filter(_.identity)
  }

  /**
   * Actions that are always available (commonplace)
   */
  @computed get catalogActionsEnd(): Array<ICatalogSearchResult> {
    const {strings, catalog: {isClientPdfAvailable, isProductView}} = this
    return [
      isClientPdfAvailable && {key: 'switchToPDFView', title: 'Switch to PDF View...', action: 'togglePDF', type: 'action', group: 'catalog', groupLabel: strings.catalogActions, icon: <PictureAsPdf/>},
      ...(
        !isProductView ? [
          {key: 'groupBy', type: 'groupBy', action: 'clear', title: 'Clear Group By', groupLabel: strings.groupByActions},
          {key: 'collapseAllGroups', title: 'Collapse All', action: 'collapseAll', type: 'groupBy'},
          {key: 'expandAllGroups', title: 'Expand All', action: 'expandAll', type: 'groupBy'}
        ] : []) as Array<ICatalogSearchResult>
    ].filter(_.identity) as ICatalogSearchResult[]
  }

  @computed get offlineItemActions(): Array<ICatalogSearchResult> {
    const {strings, validOfflineItems, workspace_offlineItemIds} = this.catalog
    return [
      workspace_offlineItemIds.length && {key: 'selectOfflineItems', title: `Select Pending Items (${workspace_offlineItemIds.length})`, action: 'selectOffline', actionLabel: 'Action', type: 'offline', group: 'offline', groupLabel: strings.offlineItems},
      ...(validOfflineItems.length > 0 ? [{key: 'publishToPLM', title: `Publish to PLM (${validOfflineItems.length})`, actionLabel: 'Action', action: 'uploadOffline', type: 'offline', group: 'offline', groupLabel: strings.offlineItems}] : [])
    ].filter(_.identity) as ICatalogSearchResult[]
  }

  @computed get catalogActionsStart(): Array<ICatalogSearchResult> {
    const {strings, catalog: {catalogItemName, isShowingClientPDF, canCreateItem, selection: {selectedIds}}} = this
    return [
      {key: 'reset_workspace', title: 'Reset Workspace', action: 'resetWorkspace', type: 'action', group: 'catalog', confirm: true, groupLabel: strings.catalogActions},
      canCreateItem && {key: 'new_catalog_item', title: `New ${catalogItemName}`, icon: icons.add, type: 'action', action: 'createCatalogItem', keywords: `add new ${catalogItemName}`, groupLabel: strings.catalogActions},
      ...(isShowingClientPDF ? [] : [
        selectedIds.size > 0 && {key: 'clear_selected_catalog_items', title: 'Clear Selection', type: 'select', action: 'clearSelection', groupLabel: strings.catalogActions},
        selectedIds.size > 0 && {key: 'toggle_inline_details', title: strings.toggleInlineDetails, type: 'select', action: 'toggleInlineDetails', groupLabel: strings.catalogActions}
      ])
    ].filter(_.identity) as ICatalogSearchResult[]
  }

  /* @computed get searchableFilterFields(): Array<ICatalogSuggestSearchResult> {
     const {filter: {filterFields}, catalog: {labelFor, _fieldByName}} = this
     return filterFields.map(
       field => (
         {
           type: 'filter',
           actionLabel: 'Filter',
           esIndex: catalog.index,
           field,
           label: labelForFieldName(field.field)
         }
       ))
   }*/

  @computed get currentWorkspaceCustomViews(): Array<ICatalogSearchResult> {
    const {workspaceConfig} = this.workspaces, groupLabel = `${workspaceConfig?.title} Workspace`
    return [
      //{title: 'Reset Workspace', type: 'catalog', action: 'resetWorkspace', groupLabel},
      ...(_.isEmpty(workspaceConfig?.customViews) ? [] : workspaceConfig.customViews.map(v => ({key: guid(), title: v.label ?? _.startCase(v._id), groupLabel, type: 'workspaceView', actionLabel: 'View', action: 'loadCustomView', value: v})))
    ]
  }

  @computed get perspectives(): Array<ICatalogSearchResult> {
    const {strings, catalog: {catalogConfig}} = this,
      perspectives = _.isEmpty(catalogConfig?.perspectives || catalogConfig?.perspectives?.length <= 1) ? [] : catalogConfig.perspectives.filter(p => !p.hidden)

    return perspectives.length > 1 ? perspectives.map(p => ({key: guid(), title: `${p.label ?? _.startCase(p.id)}`, type: 'perspective', value: p, groupLabel: strings.perspectives})) : []
  }

  @computed get catalogSearchActions() {
    const {currentWorkspaceCustomViews, cardSettings, perspectives, seasonalFields, catalogActionsStart, catalogActionsEnd, cardViewFields, catalogLibraries, groupByFields, groupBySortFields, sortFields, filtersGroup, offlineItemActions} = this
    return [
      ...catalogActionsStart,
      ...seasonalFields,
      ...currentWorkspaceCustomViews,
      ...catalogActionsEnd,
      ...offlineItemActions,
      ...catalogLibraries,
      ...cardSettings,
      ...perspectives,
      ...filtersGroup.searchEntries,
      // ...groupByFields,
      // ...cardViewFields,
      // ...groupBySortFields,
      // ...sortFields,
    ].filter(_.identity)
  }


  /**
   * Here we can create a smaller dataset of 1->many actions per 'search item'
   * The real renderer needs to see them all, this is to keep the list simple
   */
  @observable.ref searchableData: Array<ICatalogSearchResult> = []
}

class FiltersSearchGroup extends CatalogInstanceChildStore {
  searchableResultsForField = computedFn((f: ICatalogFieldConfig): Array<ICatalogSearchResult> => {
    const {catalog, stores: {filter}} = this
    const filterName = catalog.labelForFieldName_currentCatalog(f.field, 'singular', null, true),
      {groupedByValue, selected} = filter.groupFor(f.field),
      filterSearchResult = {
        key: guid(),
        type: 'filter' as CatalogSuggestSearchResultType,
        group: filterName,
        action: 'filterField',
        groupLabel: `Filters`,
        icon: 'filter',
        actionLabel: null, //'Filter',
        title: `${filterName}${selected?.size ? ` (${selected.size})` : ''}`,
        value: f
      } as ICatalogSearchResult,
      filterValues = _.values(groupedByValue).map<ICatalogSearchResult>(
        filterValue => ({
          ...filterSearchResult,
          key: guid(),
          icon: null,
          parent: filterSearchResult,
          groupLabel: null,
          actionLabel: null,
          type: 'filterValue',
          action: 'toggle',
          value: filterValue,
          title: `${filterValue.name ?? _.startCase(filterValue.compoundId)}`
        }))

    filterSearchResult.children = [
      {
        ...filterSearchResult, groupLabel: null,
        key: guid(),
        actionLabel: 'Action', title: 'Go to filter...', icon: 'filter'
      },
      selected?.size && {
        ...filterSearchResult,
        key: guid(),
        icon: icons.clearHierarchy,
        action: 'clearFilter',
        title: `Clear '${filterName}' (${selected.size})`,
        value: f,
        groupLabel: null,
        parent: filterSearchResult
      } as ICatalogSearchResult,
      {},
      ...filterValues
    ].filter(_.identity)

    //return {...filterSearchResult, children: filterValues}
    return [
      filterSearchResult
      //...filterValues
    ]
  }, {name: `searchableResultsForField`})

  @computed get offlineItemFilterItem() {
    const {strings, catalog: {filter}} = this, {groupedByValue, selected} = filter.groupFor(OFFLINE_ID)

    let filterSearchResult = {
      type: 'filter' as CatalogSuggestSearchResultType,
      group: OFFLINE_ID,
      action: 'filterField',
      groupLabel: `Filters`,
      icon: 'filter',
      actionLabel: null, //'Filter',
      title: `${strings.offlineItems}`,
      value: {field: OFFLINE_ID},
      children: []
    }

    filterSearchResult.children = [
      {
        ...filterSearchResult,
        groupLabel: null,
        actionLabel: 'Action',
        title: 'Go to filter...',
        icon: 'filter'
      },
      selected?.size && {
        ...filterSearchResult,
        icon: icons.clearHierarchy,
        action: 'clearFilter',
        title: `Clear '${strings.offlineItems}' (${selected.size})`,
        groupLabel: null,
        parent: filterSearchResult
      } as ICatalogSearchResult,
      ..._.values(groupedByValue).map<ICatalogSearchResult>(
        filterValue => ({
          ...filterSearchResult,
          icon: null,
          parent: filterSearchResult,
          groupLabel: null,
          actionLabel: null,
          type: 'filterValue',
          action: 'toggle',
          value: filterValue,
          title: `${filterValue.name ?? _.startCase(filterValue.compoundId)}`
        }))
    ].filter(_.identity)

    return filterSearchResult
  }

  @computed get searchEntries(): Array<ICatalogSearchResult> {
    const {strings, sidebar: {left: {isFiltersShowing}}, catalog: {canCreateItem, filter: {filterFields, filtersWithSelections}}} = this,
      {searchableResultsForField, offlineItemFilterItem} = this
    return _.chain([
      !isFiltersShowing && {title: 'Go to Filters...', action: 'gotoFilters', type: 'action', group: 'filter', groupLabel: strings.filters} as ICatalogSearchResult,
      filtersWithSelections.length > 0 && {title: 'Clear Filters', icon: icons.clear, action: 'clearFilters', type: 'action', group: 'filter', groupLabel: strings.filters} as ICatalogSearchResult,
      canCreateItem && offlineItemFilterItem,
      ...(filterFields ?? []).map(f => searchableResultsForField(f))
    ]).flatten().compact().value()
  }
}


// WEBPACK FOOTER //
// ./src/modules/catalogSearcher/CatalogSearcherStore.tsx